import React, { useMemo } from 'react';
import { Box, FormLabel, Stack, Typography } from '@mui/material';
import { FormComponentBuilder } from 'components/common/form/layout';
import {
    FormComponentValue,
    FormControlProps,
    FormElementControlPropsType,
} from 'components/common/form/layout/control';
import { FormElementProps } from 'components/common/form/index';
import ReactSelect from 'components/common/react-select/ReactSelect';
import useBundleTranslation from 'i18n';
import { StaticAddon } from 'components/common/static-addon/StaticAddon';
import { ALL_TYPES, resourceSearchFilter, useResourceTypeFilter, withShortLabelDesc } from './useResourceTypeFilter';
import { ResourceOption, ResourceSingleValue } from './resourceOptionParts';

interface PrimaryResourcePickerProps extends FormControlProps {
    data: Array<FormComponentValue>;
    elementProps: FormElementProps;
    showLabel: boolean;
    // Whether the knowledge-graph pool behind both pickers is empty (Form.php / the candidates
    // endpoint say so); absent, an empty option list stands in for it.
    poolEmpty?: boolean;
}

export default function PrimaryResourcePicker({
    controlProps,
}: FormElementControlPropsType<PrimaryResourcePickerProps>) {
    const { t } = useBundleTranslation('app/editor/element/extreport');
    const value = controlProps.value ? String(controlProps.value) : '';

    // Some sources (the PHP-driven forms) prepend a synthetic '--' row; the picker is clearable
    // either way, so a caller that omits it (the React panel) works identically.
    const options = useMemo(
        () => withShortLabelDesc(controlProps.data.filter((opt) => '' !== String(opt.value))),
        [controlProps.data],
    );

    const selectedKeys = useMemo(() => (value ? [value] : []), [value]);
    const { typeFilter, setTypeFilter, typeFilterOptions, filteredOptions } = useResourceTypeFilter(
        options,
        selectedKeys,
        true,
    );

    const handleChange = (newVal: string | string[]) => {
        controlProps.onChange((Array.isArray(newVal) ? newVal[0] : newVal) ?? '');
    };

    const poolEmpty = controlProps.poolEmpty ?? 0 === options.length;

    return (
        <Stack>
            {controlProps.showLabel && (
                <FormLabel>
                    <Box component={'strong'}>{t('concierge_insight_data_resources')}</Box>
                </FormLabel>
            )}
            <FormLabel>{t('concierge_primary_data_resource')}</FormLabel>
            <Stack direction="row" sx={{ mb: 2 }}>
                <StaticAddon>{t('concierge_insight_filter_by_type')}</StaticAddon>
                <Box sx={{ flexGrow: 1 }}>
                    <ReactSelect
                        data={typeFilterOptions}
                        value={typeFilter}
                        update={(newVal) => setTypeFilter((newVal as string) || ALL_TYPES)}
                        name={`${controlProps.name}_type_filter`}
                        isMulti={false}
                        isClearable={false}
                        disabled={controlProps.readOnly}
                        dataTest={`${controlProps.name}_type_filter`}
                    />
                </Box>
            </Stack>
            <ReactSelect
                data={filteredOptions}
                value={value}
                update={handleChange}
                name={controlProps.name}
                isMulti={false}
                isClearable={true}
                disabled={controlProps.readOnly}
                dataTest={controlProps.name}
                placeholder={t('concierge_insight_primary_picker_ph')}
                filterOption={resourceSearchFilter}
                noOptionsMessage={() => t('concierge_insight_resource_search_empty')}
                optionComponent={ResourceOption as any}
                overrideComponents={{ SingleValue: ResourceSingleValue }}
            />
            {poolEmpty && (
                <Typography
                    data-test="concierge_insight_resources_empty_hint"
                    sx={{ mt: 1, fontSize: '12px', lineHeight: '18px', color: 'text.secondary' }}
                >
                    {t('concierge_insight_resources_empty_hint')}
                </Typography>
            )}
        </Stack>
    );
}

export class PrimaryResourcePickerBuilder extends FormComponentBuilder {
    prepareProps(): PrimaryResourcePickerProps {
        return {
            ...this.controlProps,
            data: this.componentValues,
            elementProps: this.elementProps,
            showLabel: this.elementProps.componentProps?.showLabel ?? false,
            poolEmpty: this.elementProps.componentProps?.poolEmpty,
        };
    }
}
